import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Button,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNudges } from './NudgeProvider.js';

/** Format relative time for card header: "Just now", "10M AGO", "1H AGO", "3H AGO" */
const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}M AGO`;
  if (diffHours < 24) return `${diffHours}H AGO`;
  if (diffDays < 7) return `${diffDays}D AGO`;
  return date.toLocaleDateString();
};

/** First letter of sender name for avatar, e.g. "Sush" -> "S" */
const getSenderInitial = (senderDisplay, senderRole) => {
  if (senderDisplay && typeof senderDisplay === 'string') {
    const trimmed = senderDisplay.trim();
    if (trimmed.length > 0) return trimmed[0].toUpperCase();
  }
  if (senderRole === 'teacher') return 'T';
  if (senderRole === 'parent') return 'P';
  return '?';
};

/** Display name with role for card: "Mr. Harrison (Math)" style; we use sender_display and role */
const getSenderLabel = (senderDisplay, senderRole) => {
  const name = senderDisplay && typeof senderDisplay === 'string' ? senderDisplay.trim() : 'Unknown';
  const role = senderRole === 'teacher' ? 'Teacher' : senderRole === 'parent' ? 'Parent' : '';
  return role ? `${name} (${role})` : name;
};

export const Pop = ({ open, onClose }) => {
  const { nudges, markRead } = useNudges();

  const handleClose = async () => {
    try {
      if (nudges && nudges.length > 0) {
        for (const nudge of nudges) {
          if (nudge && nudge.id) {
            await markRead(nudge.id);
          }
        }
      }
    } catch (error) {
      console.error('Failed to mark nudges as read:', error);
    }
    onClose();
  };

  if (!nudges || nudges.length === 0) {
    return null;
  }

  const count = nudges.length;

  return (
    <Dialog
      open={open && count > 0}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 0, pr: 6 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" component="div" fontWeight="bold">
              New Messages
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              You have {count} new notification{count !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 2,
          pb: 1,
          maxHeight: '60vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': { backgroundColor: '#a8a8a8' },
          },
        }}
      >
        {nudges.map((nudge, index) => {
          const initial = getSenderInitial(nudge?.sender_display, nudge?.sender_role);
          const senderLabel = getSenderLabel(nudge?.sender_display, nudge?.sender_role);
          const timeAgo = formatTimeAgo(nudge?.created_at) || 'Unknown time';

          return (
            <Box
              key={nudge?.id || index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: 2,
                mb: 2,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                  }}
                >
                  {initial}
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    border: '2px solid',
                    borderColor: 'background.paper',
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    {senderLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {timeAgo}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.45,
                  }}
                >
                  {nudge?.text || 'No message content'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </DialogContent>

      <Box sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          fullWidth
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          Dismiss All
        </Button>
      </Box>
    </Dialog>
  );
};
